// =============================================================================
// SimSense Platform — Marketplace API Hook
// =============================================================================
// Uses mock data until backend OT-276/OT-277 land, then swap to real API calls.

import { useState, useEffect, useCallback } from "react";
import type {
  SetupProductPreview,
  SetupProductDetail,
  CreatorProfile,
  SetupReview,
  PublishedSetup,
  MarketplaceFilters,
} from "../types/marketplace";
import { api } from "../utils/api";

// ---------------------------------------------------------------------------
// Mock data — will be replaced by real API calls
// ---------------------------------------------------------------------------

const MOCK_SETUPS: SetupProductPreview[] = [
  {
    id: "1",
    carName: "Mercedes-AMG GT3 EVO 2020",
    trackName: "Daytona International Speedway - Road Course",
    setupName: "OSC Daytona Qual",
    creatorName: "Outlier Speed Co.",
    creatorSlug: "outlier-speed-co",
    isVerified: true,
    lane: "shop",
    rating: 4.8,
    reviewCount: 42,
    downloadCount: 1250,
    bestLapTime: 104.216,
    priceCents: 0,
    isFree: true,
    season: "2026S2",
    type: "Qualifying",
    skillLevel: "advanced",
    discipline: "road",
  },
  {
    id: "2",
    carName: "Dallara P217",
    trackName: "Circuit de Spa-Francorchamps - Grand Prix",
    setupName: "Craig Spa Sprint",
    creatorName: "Craig Setup Shop",
    creatorSlug: "craig-setup-shop",
    isVerified: true,
    lane: "shop",
    rating: 4.5,
    reviewCount: 18,
    downloadCount: 450,
    bestLapTime: 121.5,
    priceCents: 299,
    isFree: false,
    season: "2026S2",
    type: "Race",
    skillLevel: "intermediate",
    discipline: "road",
  },
  {
    id: "3",
    carName: "NASCAR Cup Series Chevrolet Camaro ZL1",
    trackName: "Rockingham Speedway",
    setupName: "Maconi Rocky Loose",
    creatorName: "Maconi Setup Shop",
    creatorSlug: "maconi-setup-shop",
    isVerified: true,
    lane: "shop",
    rating: 4.9,
    reviewCount: 88,
    downloadCount: 3200,
    bestLapTime: 23.15,
    priceCents: 499,
    isFree: false,
    season: "2026S2",
    type: "Race",
    skillLevel: "advanced",
    discipline: "oval",
  },
  {
    id: "4",
    carName: "Ferrari 296 GT3",
    trackName: "Road Atlanta",
    setupName: "Safe Endurance 24h",
    creatorName: "JohnDoeRacer",
    isVerified: false,
    lane: "community",
    rating: 4.2,
    reviewCount: 5,
    downloadCount: 120,
    bestLapTime: 78.4,
    priceCents: 0,
    isFree: true,
    season: "2026S2",
    type: "Race",
    skillLevel: "beginner",
    discipline: "road",
  },
  {
    id: "5",
    carName: "Porsche 911 GT3 R (992)",
    trackName: "Sebring International Raceway",
    setupName: "OSC Sebring Race",
    creatorName: "Outlier Speed Co.",
    creatorSlug: "outlier-speed-co",
    isVerified: true,
    lane: "shop",
    rating: 4.9,
    reviewCount: 112,
    downloadCount: 3100,
    bestLapTime: 118.9,
    priceCents: 299,
    isFree: false,
    season: "2026S2",
    type: "Race",
    skillLevel: "intermediate",
    discipline: "road",
  },
  {
    id: "6",
    carName: "BMW M4 GT3",
    trackName: "Nurburgring Combined",
    setupName: "Nordschleife Endurance",
    creatorName: "Craig Setup Shop",
    creatorSlug: "craig-setup-shop",
    isVerified: true,
    lane: "shop",
    rating: 4.7,
    reviewCount: 31,
    downloadCount: 890,
    bestLapTime: 481.2,
    priceCents: 399,
    isFree: false,
    season: "2026S2",
    type: "Race",
    skillLevel: "advanced",
    discipline: "road",
  },
  {
    id: "7",
    carName: "Audi R8 LMS EVO II GT3",
    trackName: "Watkins Glen International - Boot",
    setupName: "WGI Race Stable",
    creatorName: "FastAlien_GT3",
    isVerified: true,
    lane: "pro_builder",
    rating: 4.6,
    reviewCount: 14,
    downloadCount: 340,
    bestLapTime: 107.8,
    priceCents: 199,
    isFree: false,
    season: "2026S2",
    type: "Race",
    skillLevel: "intermediate",
    discipline: "road",
  },
  {
    id: "8",
    carName: "Mazda MX-5 Cup",
    trackName: "Summit Point Raceway",
    setupName: "Rookie Friendly Race",
    creatorName: "HelpfulDriver",
    isVerified: false,
    lane: "community",
    rating: 4.4,
    reviewCount: 22,
    downloadCount: 580,
    bestLapTime: 72.3,
    priceCents: 0,
    isFree: true,
    season: "2026S1",
    type: "Race",
    skillLevel: "beginner",
    discipline: "road",
  },
  {
    id: "9",
    carName: "Lamborghini Huracan GT3 EVO",
    trackName: "Mount Panorama Circuit",
    setupName: "Bathurst Qual Aggro",
    creatorName: "OvalToRoad",
    isVerified: true,
    lane: "pro_builder",
    rating: 4.3,
    reviewCount: 9,
    downloadCount: 210,
    bestLapTime: 121.1,
    priceCents: 349,
    isFree: false,
    season: "2026S1",
    type: "Qualifying",
    skillLevel: "alien",
    discipline: "road",
  },
];

const MOCK_DETAIL: SetupProductDetail = {
  ...MOCK_SETUPS[0],
  description:
    "Aggressive low downforce qualifying setup optimized for late braking into Turn 1. Features a stiff rear spring to keep rotation through the bus stop. Best suited for drivers comfortable with a loose rear end on entry.",
  tags: ["Low Downforce", "Aggressive", "Qualifying", "Oversteer"],
  conditions: { airTemp: 78, trackTemp: 95, windSpeed: 2, humidity: 45 },
  lastUpdated: "2 days ago",
  skillLevel: "advanced",
  creatorId: "osc-1",
};

const MOCK_REVIEWS: SetupReview[] = [
  {
    id: "r1",
    userId: "u1",
    userName: "SpeedKing42",
    rating: 5,
    reviewText: "Perfect for my driving style. Nailed a 1:44.8 on my second session.",
    userIrating: 3200,
    lapsDriven: 45,
    bestLapTime: 104.8,
    createdAt: "2026-03-12",
  },
  {
    id: "r2",
    userId: "u2",
    userName: "RookieRacer",
    rating: 4,
    reviewText: "Bit loose in the bus stop but fast everywhere else.",
    userIrating: 1800,
    lapsDriven: 22,
    bestLapTime: 106.1,
    createdAt: "2026-03-10",
  },
  {
    id: "r3",
    userId: "u3",
    userName: "EnduranceElite",
    rating: 3,
    reviewText: "Hard to drive consistently under 2k iRating. Great for hotlaps though.",
    userIrating: 2500,
    lapsDriven: 60,
    bestLapTime: 108.3,
    createdAt: "2026-03-08",
  },
];

const MOCK_CREATOR: CreatorProfile = {
  userId: "osc-1",
  slug: "outlier-speed-co",
  creatorType: "shop_owner",
  shopName: "Outlier Speed Co.",
  shopDescription:
    "Professional telemetry-driven setups built by top iRacing competitors. We focus on drivability and consistent race pace for endurance events.",
  isVerified: true,
  totalSetups: 24,
  avgRating: 4.8,
  totalDownloads: 15200,
  featuredProductIds: ["1", "5"],
};

const MOCK_PUBLISHED: PublishedSetup[] = [
  {
    id: "1",
    name: "OSC Daytona Qual",
    car: "Mercedes-AMG GT3",
    trackName: "Daytona International Speedway - Road Course",
    season: "2026S2",
    status: "active",
    downloads: 1250,
    revenue: 0,
    price: "Free",
    priceCents: 0,
    isFree: true,
    type: "qualifying",
    tags: ["Low Downforce", "Qualifying"],
    description: "",
    telemetrySessionId: "session-1",
    updatedAt: "2026-03-10T00:00:00Z",
  },
  {
    id: "5",
    name: "OSC Sebring Race",
    car: "Porsche 911 GT3 R",
    trackName: "Sebring International Raceway",
    season: "2026S2",
    status: "active",
    downloads: 3100,
    revenue: 9269,
    price: "$2.99",
    priceCents: 299,
    isFree: false,
    type: "race",
    tags: ["Long Run"],
    description: "",
    telemetrySessionId: "session-5",
    updatedAt: "2026-03-10T00:00:00Z",
  },
  {
    id: "8",
    name: "Old Spa Sprint",
    car: "Dallara P217",
    trackName: "Circuit de Spa-Francorchamps - Grand Prix",
    season: "2026S2",
    status: "needs_update",
    downloads: 450,
    revenue: 1345,
    price: "$2.99",
    priceCents: 299,
    isFree: false,
    type: "race",
    tags: ["Short Run"],
    description: "",
    telemetrySessionId: "session-8",
    updatedAt: "2026-03-10T00:00:00Z",
  },
];

// ---------------------------------------------------------------------------
// Setup of the Week (admin-curated spotlight)
// ---------------------------------------------------------------------------

export interface SetupOfTheWeek {
  setup: SetupProductPreview;
  editorNote: string; // Short blurb from admin/editor
  weekLabel: string;  // e.g., "March 10–16, 2026"
}

const MOCK_SOTW: SetupOfTheWeek = {
  setup: MOCK_SETUPS[4], // OSC Sebring Race — high rated, high downloads
  editorNote:
    "This week's spotlight goes to Outlier Speed Co.'s Sebring Race setup — 112 reviews with a 4.9 average. Drivers praise its late-braking stability into Turn 1 and consistent tire life over long stints.",
  weekLabel: "March 10–16, 2026",
};

// ---------------------------------------------------------------------------
// Rising Creators (engagement recognition)
// ---------------------------------------------------------------------------

export interface RisingCreator {
  name: string;
  slug?: string;
  isVerified: boolean;
  totalDownloads: number;
  avgRating: number;
  setupCount: number;
  badge: "rising" | "top_rated" | "most_downloaded";
}

const MOCK_RISING_CREATORS: RisingCreator[] = [
  { name: "FastAlien_GT3", slug: undefined, isVerified: true, totalDownloads: 340, avgRating: 4.6, setupCount: 1, badge: "rising" },
  { name: "HelpfulDriver", slug: undefined, isVerified: false, totalDownloads: 580, avgRating: 4.4, setupCount: 1, badge: "rising" },
  { name: "OvalToRoad", slug: undefined, isVerified: true, totalDownloads: 210, avgRating: 4.3, setupCount: 1, badge: "rising" },
];

void MOCK_DETAIL;
void MOCK_REVIEWS;
void MOCK_CREATOR;
void MOCK_PUBLISHED;
void MOCK_SOTW;
void MOCK_RISING_CREATORS;

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

interface MarketplaceListingApi {
  id: string;
  car_name: string;
  track_name: string;
  setup_name: string;
  creator_name: string;
  creator_avatar_url?: string | null;
  creator_slug?: string | null;
  is_verified: boolean;
  lane: "shop" | "pro_builder" | "community";
  rating: number;
  review_count: number;
  download_count: number;
  best_lap_time: number;
  price_cents: number;
  is_free: boolean;
  season: string;
  type: string;
  discipline?: "road" | "oval" | "dirt_road" | "dirt_oval" | null;
  skill_level?: string | null;
  telemetry_session_id?: string | null;
}

interface MarketplaceDetailApi extends MarketplaceListingApi {
  description: string;
  tags: string[];
  conditions?: {
    air_temp?: number | null;
    track_temp?: number | null;
    wind_speed?: number | null;
    humidity?: number | null;
  } | null;
  last_updated: string;
  iracing_build?: string | null;
  creator_id: string;
}

interface MarketplaceReviewApi {
  id: string;
  user_id: string;
  user_name: string;
  rating: number;
  review_text?: string | null;
  user_irating?: number | null;
  laps_driven?: number | null;
  best_lap_time?: number | null;
  created_at: string;
}

interface MarketplaceBrowseResponse {
  items: MarketplaceListingApi[];
  available_tracks: string[];
}

interface MarketplaceDetailResponse {
  listing: MarketplaceDetailApi;
  reviews: MarketplaceReviewApi[];
}

interface CreatorProfileApi {
  user_id: string;
  slug: string;
  creator_type: "community" | "pro_builder" | "shop_owner";
  shop_name: string;
  shop_description: string;
  shop_logo_url?: string | null;
  shop_banner_url?: string | null;
  is_verified: boolean;
  total_setups: number;
  avg_rating: number;
  total_downloads: number;
  iracing_irating?: number | null;
  iracing_license?: string | null;
  featured_product_ids?: string[];
}

interface CreatorProfileResponse {
  profile: CreatorProfileApi;
  listings: MarketplaceListingApi[];
}

interface CreatorDashboardStatsApi {
  total_downloads: number;
  total_earnings: number;
  avg_rating: number;
}

interface CreatorPublishedSetupApi {
  id: string;
  name: string;
  car: string;
  track_name: string;
  season: string;
  status: "active" | "needs_update" | "draft" | "delisted";
  downloads: number;
  revenue: number;
  price: string;
  price_cents: number;
  is_free: boolean;
  type: "race" | "qualifying" | "baseline";
  tags: string[];
  description: string;
  telemetry_session_id?: string | null;
  updated_at: string;
}

interface CreatorListingsResponse {
  items: CreatorPublishedSetupApi[];
  stats: CreatorDashboardStatsApi;
}

interface CreatorListingUpdateRequest {
  setupName?: string;
  type?: "race" | "qualifying" | "baseline";
  tags?: string[];
  priceCents?: number;
  isFree?: boolean;
  description?: string;
  status?: "active" | "needs_update" | "draft" | "delisted";
}

interface MarketplacePublishRequest {
  sessionId: string;
  setupName: string;
  type: "race" | "qualifying" | "baseline";
  tags: string[];
  priceCents: number;
  isFree: boolean;
  description?: string;
}

function normalizeMarketplaceListing(listing: MarketplaceListingApi): SetupProductPreview {
  return {
    id: listing.id,
    carName: listing.car_name,
    trackName: listing.track_name,
    setupName: listing.setup_name,
    creatorName: listing.creator_name,
    creatorAvatarUrl: listing.creator_avatar_url || undefined,
    creatorSlug: listing.creator_slug || undefined,
    isVerified: listing.is_verified,
    lane: listing.lane,
    rating: listing.rating,
    reviewCount: listing.review_count,
    downloadCount: listing.download_count,
    bestLapTime: listing.best_lap_time,
    priceCents: listing.price_cents,
    isFree: listing.is_free,
    season: listing.season,
    type: listing.type,
    discipline: listing.discipline || undefined,
    skillLevel: listing.skill_level || undefined,
    telemetrySessionId: listing.telemetry_session_id || undefined,
  };
}

function normalizeMarketplaceDetail(detail: MarketplaceDetailApi): SetupProductDetail {
  return {
    ...normalizeMarketplaceListing(detail),
    description: detail.description,
    tags: detail.tags || [],
    conditions: {
      airTemp: detail.conditions?.air_temp ?? 0,
      trackTemp: detail.conditions?.track_temp ?? 0,
      windSpeed: detail.conditions?.wind_speed ?? undefined,
      humidity: detail.conditions?.humidity ?? undefined,
    },
    lastUpdated: detail.last_updated,
    iracingBuild: detail.iracing_build || undefined,
    creatorId: detail.creator_id,
  };
}

function normalizeMarketplaceReview(review: MarketplaceReviewApi): SetupReview {
  return {
    id: review.id,
    userId: review.user_id,
    userName: review.user_name,
    rating: review.rating,
    reviewText: review.review_text || undefined,
    userIrating: review.user_irating || undefined,
    lapsDriven: review.laps_driven || undefined,
    bestLapTime: review.best_lap_time || undefined,
    createdAt: review.created_at,
  };
}

function normalizeCreatorProfile(profile: CreatorProfileApi): CreatorProfile {
  return {
    userId: profile.user_id,
    slug: profile.slug,
    creatorType: profile.creator_type,
    shopName: profile.shop_name,
    shopDescription: profile.shop_description,
    shopLogoUrl: profile.shop_logo_url || undefined,
    shopBannerUrl: profile.shop_banner_url || undefined,
    isVerified: profile.is_verified,
    totalSetups: profile.total_setups,
    avgRating: profile.avg_rating,
    totalDownloads: profile.total_downloads,
    iracingIrating: profile.iracing_irating || undefined,
    iracingLicense: profile.iracing_license || undefined,
    featuredProductIds: profile.featured_product_ids || [],
  };
}

function normalizeCreatorDashboardStats(stats?: CreatorDashboardStatsApi) {
  return {
    totalDownloads: stats?.total_downloads || 0,
    totalEarnings: stats?.total_earnings || 0,
    avgRating: stats?.avg_rating || 0,
  };
}

function normalizePublishedSetup(setup: CreatorPublishedSetupApi): PublishedSetup {
  return {
    id: setup.id,
    name: setup.name,
    car: setup.car,
    trackName: setup.track_name,
    season: setup.season,
    status: setup.status,
    downloads: setup.downloads,
    revenue: setup.revenue,
    price: setup.price,
    priceCents: setup.price_cents,
    isFree: setup.is_free,
    type: setup.type,
    tags: setup.tags || [],
    description: setup.description,
    telemetrySessionId: setup.telemetry_session_id || undefined,
    updatedAt: setup.updated_at,
  };
}

function normalizeSetupOfTheWeek(sotw: {
  setup: MarketplaceListingApi;
  editor_note: string;
  week_label: string;
}): SetupOfTheWeek {
  return {
    setup: normalizeMarketplaceListing(sotw.setup),
    editorNote: sotw.editor_note,
    weekLabel: sotw.week_label,
  };
}

function normalizeRisingCreator(creator: {
  name: string;
  slug?: string | null;
  is_verified: boolean;
  total_downloads: number;
  avg_rating: number;
  setup_count: number;
  badge: "rising" | "top_rated" | "most_downloaded";
}): RisingCreator {
  return {
    name: creator.name,
    slug: creator.slug || undefined,
    isVerified: creator.is_verified,
    totalDownloads: creator.total_downloads,
    avgRating: creator.avg_rating,
    setupCount: creator.setup_count,
    badge: creator.badge,
  };
}

export function useMarketplaceBrowse(filters: MarketplaceFilters) {
  const [setups, setSetups] = useState<SetupProductPreview[]>([]);
  const [loading, setLoading] = useState(true);
  const [availableTracks, setAvailableTracks] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;

    async function fetchBrowse() {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filters.search) params.set("search", filters.search);
        if (filters.showFree) params.set("show_free", "true");
        if (filters.showPremium) params.set("show_premium", "true");
        if (filters.laneFilter && filters.laneFilter !== "all") params.set("lane", filters.laneFilter);
        if (filters.season) params.set("season", filters.season);
        if (filters.track) params.set("track", filters.track);
        if (filters.discipline) params.set("discipline", filters.discipline);
        if (filters.car) params.set("car", filters.car);
        if (filters.type) params.set("type", filters.type);

        const query = params.toString();
        const data = await api<MarketplaceBrowseResponse>(
          `/api/marketplace/browse${query ? `?${query}` : ""}`,
        );

        if (cancelled) return;
        setSetups((data.items || []).map(normalizeMarketplaceListing));
        setAvailableTracks(data.available_tracks || []);
      } catch (error) {
        console.error("Failed to load marketplace browse", error);
        if (cancelled) return;
        setSetups([]);
        setAvailableTracks([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void fetchBrowse();
    return () => {
      cancelled = true;
    };
  }, [
    filters.search,
    filters.showFree,
    filters.showPremium,
    filters.laneFilter,
    filters.season,
    filters.track,
    filters.discipline,
    filters.car,
    filters.type,
  ]);

  return { setups, loading, availableTracks };
}

export function useSetupOfTheWeek() {
  const [sotw, setSotw] = useState<SetupOfTheWeek | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchSpotlight() {
      setLoading(true);
      try {
        const data = await api<{
          setup: MarketplaceListingApi;
          editor_note: string;
          week_label: string;
        } | null>("/api/marketplace/spotlight");
        if (!cancelled) setSotw(data ? normalizeSetupOfTheWeek(data) : null);
      } catch (error) {
        console.error("Failed to load marketplace spotlight", error);
        if (!cancelled) setSotw(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void fetchSpotlight();
    return () => {
      cancelled = true;
    };
  }, []);

  return { sotw, loading };
}

export function useRisingCreators() {
  const [creators, setCreators] = useState<RisingCreator[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchCreators() {
      setLoading(true);
      try {
        const data = await api<Array<{
          name: string;
          slug?: string | null;
          is_verified: boolean;
          total_downloads: number;
          avg_rating: number;
          setup_count: number;
          badge: "rising" | "top_rated" | "most_downloaded";
        }>>("/api/marketplace/rising-creators");
        if (!cancelled) setCreators((data || []).map(normalizeRisingCreator));
      } catch (error) {
        console.error("Failed to load rising creators", error);
        if (!cancelled) setCreators([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void fetchCreators();
    return () => {
      cancelled = true;
    };
  }, []);

  return { creators, loading };
}

export function useSetupDetail(productId: string | undefined) {
  const [detail, setDetail] = useState<SetupProductDetail | null>(null);
  const [reviews, setReviews] = useState<SetupReview[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!productId) {
      setDetail(null);
      setReviews([]);
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchDetail() {
      setLoading(true);
      try {
        const data = await api<MarketplaceDetailResponse>(`/api/marketplace/${productId}`);
        if (cancelled) return;
        setDetail(data.listing ? normalizeMarketplaceDetail(data.listing) : null);
        setReviews((data.reviews || []).map(normalizeMarketplaceReview));
      } catch (error) {
        console.error("Failed to load marketplace detail", error);
        if (cancelled) return;
        setDetail(null);
        setReviews([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void fetchDetail();
    return () => {
      cancelled = true;
    };
  }, [productId]);

  return { detail, reviews, loading };
}

export function useCreatorProfile(slug: string | undefined) {
  const [profile, setProfile] = useState<CreatorProfile | null>(null);
  const [setups, setSetups] = useState<SetupProductPreview[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) {
      setProfile(null);
      setSetups([]);
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchProfile() {
      setLoading(true);
      try {
        const data = await api<CreatorProfileResponse>(`/api/marketplace/shop/${slug}`);
        if (cancelled) return;
        setProfile(data.profile ? normalizeCreatorProfile(data.profile) : null);
        setSetups((data.listings || []).map(normalizeMarketplaceListing));
      } catch (error) {
        console.error("Failed to load creator profile", error);
        if (cancelled) return;
        setProfile(null);
        setSetups([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void fetchProfile();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return { profile, setups, loading };
}

export function useCreatorDashboard() {
  const [publishedSetups, setPublishedSetups] = useState<PublishedSetup[]>([]);
  const [stats, setStats] = useState({ totalDownloads: 0, totalEarnings: 0, avgRating: 0 });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api<CreatorListingsResponse>("/api/marketplace/my-listings");
      setPublishedSetups((data.items || []).map(normalizePublishedSetup));
      setStats(normalizeCreatorDashboardStats(data.stats));
    } catch (error) {
      console.error("Failed to load creator dashboard", error);
      setPublishedSetups([]);
      setStats({ totalDownloads: 0, totalEarnings: 0, avgRating: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  const publishListing = useCallback(async (payload: MarketplacePublishRequest) => {
    const created = await api<CreatorPublishedSetupApi>("/api/marketplace/publish", {
      method: "POST",
      body: JSON.stringify({
        session_id: payload.sessionId,
        setup_name: payload.setupName,
        type: payload.type,
        tags: payload.tags,
        price_cents: payload.priceCents,
        is_free: payload.isFree,
        description: payload.description,
      }),
    });
    await refresh();
    return normalizePublishedSetup(created);
  }, [refresh]);

  const updateListing = useCallback(async (listingId: string, payload: CreatorListingUpdateRequest) => {
    const updated = await api<CreatorPublishedSetupApi>(`/api/marketplace/${listingId}`, {
      method: "PUT",
      body: JSON.stringify({
        setup_name: payload.setupName,
        type: payload.type,
        tags: payload.tags,
        price_cents: payload.priceCents,
        is_free: payload.isFree,
        description: payload.description,
        status: payload.status,
      }),
    });
    await refresh();
    return normalizePublishedSetup(updated);
  }, [refresh]);

  const removeListing = useCallback(async (listingId: string) => {
    const removed = await api<CreatorPublishedSetupApi>(`/api/marketplace/${listingId}`, {
      method: "DELETE",
    });
    await refresh();
    return normalizePublishedSetup(removed);
  }, [refresh]);

  const relistListing = useCallback(async (listingId: string) => {
    return updateListing(listingId, { status: "active" });
  }, [updateListing]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return {
    publishedSetups,
    stats,
    loading,
    refresh,
    publishListing,
    updateListing,
    removeListing,
    relistListing,
  };
}
